import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { DataGrid } from '@mui/x-data-grid'
import { get } from 'lodash'
import CloseButton from 'src/components/common/CloseButton'
import { COLORS } from 'src/utils/Themes/colors'
import { INSURANCE_NAMES } from 'src/utils/constants/InsuranceNames'
import { formatTableCellValue } from 'src/utils/commonMethods'
import formatToINR from 'src/utils/commonMethods/formatToINR'
import formatDate from 'src/utils/commonMethods/formatDate'
import { Tooltip } from '@mui/material'
import {
  CLAIM_AMOUNT_STATUS,
  DETAILED_VIEW_TYPES,
} from 'src/utils/constants/RiskReports'
import { getFinancialYearByDate } from 'src/utils/commonMethods/FinancialYear'

const DetailedView = ({ data, date, closefunction, type, isFinancialView }) => {
  let employeeBenefits
  if (type === DETAILED_VIEW_TYPES?.EMPLOYEE_BENEFIT_CLAIMS) {
    employeeBenefits = data
  } else {
    employeeBenefits = get(data, 'employeeBenefits', [])
  }

  let nonEmployeeBenefits =
    type !== DETAILED_VIEW_TYPES?.GROUP_ACCIDENTAL &&
    DETAILED_VIEW_TYPES?.EMPLOYEE_BENEFIT_CLAIMS
      ? get(data, 'nonEmployeeBenefits', [])
      : null
  nonEmployeeBenefits = nonEmployeeBenefits === null ? [] : nonEmployeeBenefits

  let employeeBenefitsRows
  employeeBenefits = employeeBenefits === null ? [] : employeeBenefits
  if (type === DETAILED_VIEW_TYPES?.GROUP_ACCIDENTAL) {
    employeeBenefitsRows = employeeBenefits
      ?.filter(
        (x) => x?.policyName === INSURANCE_NAMES?.GROUP_ACCIDENTAL_INSURANCE
      )
      ?.map((d, i) => ({
        ...d,
        id: i,
      }))
  } else if (type === DETAILED_VIEW_TYPES?.EMPLOYEE_BENEFIT_CLAIMS) {
    employeeBenefitsRows = employeeBenefits
      ?.filter(
        (item) => item?.policyName === INSURANCE_NAMES?.GROUP_TERM_INSURANCE
      )
      ?.map((d, i) => ({
        ...d,
        id: i,
      }))
  } else {
    employeeBenefitsRows = employeeBenefits?.map((d, i) => ({
      ...d,
      id: i,
    }))
  }

  const employeeBenefitsColumns = [
    {
      field: 'policyName',
      headerName: 'Policy Name',
      valueGetter: (params) =>
        formatTableCellValue('policyName', params.row.policyName),
    },
    {
      field: 'policyNumber',
      headerName: 'Policy Number',
      valueGetter: (params) =>
        formatTableCellValue('policyNumber', params.row.policyNumber),
    },
    {
      field: 'employeeId',
      headerName: 'Employee Id',
      valueGetter: (params) =>
        formatTableCellValue('employeeId', params.row.employeeId),
    },
    {
      field: 'employeeName',
      headerName: 'Employee Name',
      valueGetter: (params) =>
        formatTableCellValue('employeeName', params.row.employeeName),
    },
    {
      field: 'claimantName',
      headerName: 'Claimant Name',
      valueGetter: (params) =>
        formatTableCellValue('claimantName', params.row.claimantName),
    },
    {
      field: 'relation',
      headerName: 'Relation',
      valueGetter: (params) =>
        formatTableCellValue('relation', params.row.relation),
    },
    {
      field: 'dateOfBirth',
      headerName: 'Date of Birth',
      valueGetter: (params) =>
        formatTableCellValue('dateOfBirth', params.row.dateOfBirth),
    },
    {
      field: 'claimantAge',
      headerName: 'Claimant Age',
      valueGetter: (params) =>
        formatTableCellValue('claimantAge', params.row.claimantAge),
    },
    {
      field: 'claimantGender',
      headerName: 'Claimant Gender',
      valueGetter: (params) =>
        formatTableCellValue('claimantGender', params.row.gender),
    },
    {
      field: 'dateOfAdmission',
      headerName: 'Date of Admission',
      valueGetter: (params) =>
        formatTableCellValue('dateOfAdmission', params.row.dateOfAdmission),
    },
    {
      field: 'dateOfDischarge',
      headerName: 'Date of Discharge',
      valueGetter: (params) =>
        formatTableCellValue('dateOfDischarge', params.row.dateOfDischarge),
    },
    {
      field: 'ailment',
      headerName: 'Ailment',
      valueGetter: (params) =>
        formatTableCellValue('ailment', params.row.ailment),
    },
    {
      field: 'pendingFrom',
      headerName: 'Pending Since',
      valueGetter: (params) =>
        formatTableCellValue('pendingFrom', params.row.pendingFrom),
    },
    {
      field: 'financialYear',
      headerName: 'Financial Year',
      valueGetter: (params) =>
        formatTableCellValue(
          'financialYear',
          getFinancialYearByDate(params.row.pendingFrom).text
        ),
    },
    {
      field: 'amountClaimed',
      headerName: 'Claimed Amount',
      valueGetter: (params) =>
        formatTableCellValue('amountClaimed', params.row.amountClaimed),
    },
    {
      field: 'finalApprovedAmount',
      headerName: 'Final Approved Amount',
      valueGetter: (params) =>
        formatTableCellValue(
          'finalApprovedAmount',
          params.row.finalApprovedAmount
        ),
    },
    {
      field: 'claimType',
      headerName: 'Claim Type',
      valueGetter: (params) =>
        formatTableCellValue('claimType', params.row.claimType),
    },
    {
      field: 'claimStatus',
      headerName: 'Claim Status',
      valueGetter: (params) =>
        formatTableCellValue('claimStatus', params.row.claimStatus),
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      valueGetter: (params) =>
        formatTableCellValue('remarks', params.row.remarks),
      renderCell: (params) => (
        <Tooltip title={params.row.remarks}>
          <span>{formatTableCellValue('remarks', params.row.remarks)}</span>
        </Tooltip>
      ),
    },
  ]

  const nonEmployeeBenefitsRows =
    type !== DETAILED_VIEW_TYPES?.GROUP_ACCIDENTAL
      ? nonEmployeeBenefits?.map((d, j) => ({
          ...d,
          id: j,
          estClaimAmount: `₹ ${formatToINR(d?.estClaimAmount)}`,
          dateOfLoss: formatDate(d?.dateOfLoss),
        }))
      : null

  const nonEmployeeBenefitsColumns = [
    {
      field: 'dateOfLoss',
      headerName: 'Year',
      valueGetter: (params) =>
        formatTableCellValue('dateOfLoss', params?.row?.dateOfLoss),
    },
    {
      field: 'financialYear',
      headerName: 'Financial Year',
      valueGetter: (params) =>
        formatTableCellValue(
          'financialYear',
          getFinancialYearByDate(params?.row?.dateOfLoss).text
        ),
    },
    { field: 'policyName', headerName: 'Policy Name' },
    {
      field: 'policyNumber',
      headerName: 'Policy Number',
    },
    {
      field: 'insuranceCompanyName',
      headerName: 'Insurance Company Name',
    },
    {
      field: 'settledClaimAmount',
      headerName: 'Claim Settled Amount',
      valueGetter: (params) =>
        formatTableCellValue(
          'settledClaimAmount',
          params?.row?.settledClaimAmount
        ),
    },
    {
      field: 'outstandingClaimAmount',
      headerName: 'Outstanding Claim Amount',
      valueGetter: (params) =>
        formatTableCellValue(
          'outstandingClaimAmount',
          params?.row?.outstandingClaimAmount
        ),
    },

    { field: 'claimStatus', headerName: 'Claim Status' },
    {
      field: 'natureOfLoss',
      headerName: 'Nature Of Loss',
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      renderCell: (params) => (
        <Tooltip title={params?.row?.remarks}>
          <span className='cursor-pointer'>
            {formatTableCellValue('remarks', params?.row?.remarks)}
          </span>
        </Tooltip>
      ),
    },
  ]

  const classes = useStyles()

  const [claimAmountStats, setClaimAmountStats] = useState({
    ebSettledClaimAmount: 0,
    ebOutstandingClaimAmount: 0,
    nonEbSettledClaimAmount: 0,
    nonEbOutstandingClaimAmount: 0,
  })

  const getClaimAmountStats = () => {
    let updatedStats = { ...claimAmountStats }
    employeeBenefitsRows?.forEach((rowItem) => {
      switch (rowItem.claimStatus) {
        case CLAIM_AMOUNT_STATUS?.OUTSTANDING:
          updatedStats.ebOutstandingClaimAmount += isNaN(rowItem?.amountClaimed)
            ? 0
            : Number(rowItem?.amountClaimed)
          break
        case CLAIM_AMOUNT_STATUS?.SETTLED:
          updatedStats.ebSettledClaimAmount += isNaN(
            rowItem?.finalApprovedAmount
          )
            ? 0
            : Number(rowItem?.finalApprovedAmount)
          break
        default:
          break
      }
    })

    nonEmployeeBenefitsRows?.map((rowItem) => {
      updatedStats.nonEbSettledClaimAmount += isNaN(rowItem?.settledClaimAmount)
        ? 0
        : Number(rowItem?.settledClaimAmount)

      updatedStats.nonEbOutstandingClaimAmount += isNaN(
        rowItem?.outstandingClaimAmount
      )
        ? 0
        : Number(rowItem?.outstandingClaimAmount)
    })

    setClaimAmountStats({ ...updatedStats })
  }

  useEffect(() => {
    getClaimAmountStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.title}>
          {!isFinancialView ? (
            <span>Claims Synopsis as on {date}</span>
          ) : (
            <span> Financial Claims Synopsis as on {date}</span>
          )}
          <CloseButton
            classname={classes.closeBtn}
            title='Close'
            handleClose={closefunction}
          />
        </div>
        <div className={classes.content}>
          <div className={classes.employeeBenefitsContainer}>
            <h2>Employee Benefits:</h2>
            <DataGrid
              rows={employeeBenefitsRows}
              columns={employeeBenefitsColumns}
              autoHeight
              disableColumnMenu
              disableColumnSelector
              pageSize={5}
              isRowSelectable={() => false}
              className={classes.dataGrid}
            />
            <div className={classes.totalAmountContainer}>
              <div className={classes.totalAmountSubContainer}>
                <diva className={classes.totalHeading}>
                  Total Claim Settled Amount :
                </diva>
                <div className={classes.totalValue}>
                  {formatToINR(claimAmountStats?.ebSettledClaimAmount)}
                </div>
              </div>
              <div className={classes.totalAmountSubContainer}>
                <div className={classes.totalHeading}>
                  Total Outstanding Amount :
                </div>
                <div className={classes.totalValue}>
                  {formatToINR(claimAmountStats?.ebOutstandingClaimAmount)}
                </div>
              </div>
            </div>
          </div>
          {type !== DETAILED_VIEW_TYPES?.EMPLOYEE_BENEFIT_CLAIMS &&
          type !== DETAILED_VIEW_TYPES?.GROUP_ACCIDENTAL ? (
            <div className={classes.content}>
              <div className={classes.employeeBenefitsContainer}>
                <h2>Non Employee Benefits:</h2>
                <DataGrid
                  rows={nonEmployeeBenefitsRows}
                  columns={nonEmployeeBenefitsColumns}
                  autoHeight
                  disableColumnMenu
                  disableColumnSelector
                  pageSize={5}
                  isRowSelectable={() => false}
                  className={classes.employeeBenefitsDatagrid}
                />
                <div className={classes.totalAmountContainer}>
                  <div className={classes.totalAmountSubContainer}>
                    <diva className={classes.totalHeading}>
                      Total Claim Settled Amount :
                    </diva>
                    <div className={classes.totalValue}>
                      {formatToINR(claimAmountStats?.nonEbSettledClaimAmount)}
                    </div>
                  </div>
                  <div className={classes.totalAmountSubContainer}>
                    <div className={classes.totalHeading}>
                      Total Outstanding Amount :
                    </div>
                    <div className={classes.totalValue}>
                      {formatToINR(
                        claimAmountStats?.nonEbOutstandingClaimAmount
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default DetailedView

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%',
    padding: '24px',
    boxSizing: 'border-box',
    position: 'relative',
  },
  content: {
    '& h2': {
      fontSize: '16px',
      fontWeight: 400,
      textAlign: 'left',
      margin: '15px 0 10px 0',
      color: COLORS.DARK_PINK,
      '& first-child': {
        marginTop: 0,
      },
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: '45px',
      '& .MuiTablePagination-displayedRows': {
        padding: 0,
        margin: 0,
      },
      '& .MuiButtonBase-root': {
        padding: '6px',
      },
    },
    '& .MuiDataGrid-root': {
      background: '#f9f9f9',
    },
  },
  employeeBenefitsDatagrid: {
    '& .MuiDataGrid-columnHeader': {
      minWidth: '150px !important',
      width: '100%',
      whiteSpace: 'unset !important',
    },
    '& .MuiDataGrid-cell': {
      minWidth: '150px !important',
      width: '100%',
      padding: '5px !important',
      justifyContent: 'flex-start !important',
      wordBreak: 'break-word',
      lineHeight: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiDataGrid-virtualScroller': {
      '@media (min-width: 1025px) and (max-width: 1440px)': {
        overflow: 'auto !important',
      },
    },
  },
  tableCell: {
    paddingLeft: '15px !important',
    maxWidth: 'fit-content !important',
  },
  dataGrid: {
    '& .MuiDataGrid-columnHeader': {
      minWidth: '150px !important',
      width: '100%',
      whiteSpace: 'unset !important',
    },
    '& .MuiDataGrid-cell': {
      minWidth: '150px !important',
      width: '100%',
      whiteSpace: 'unset !important',
      padding: '5px !important',
      justifyContent: 'flex-start !important',
      wordBreak: 'break-word',
      lineHeight: 1,
    },
  },
  closeBtn: {
    position: 'absolute',
    right: 0,
  },
  title: {
    fontSize: '22px',
    padding: '0px 100px 10px 0px',
    margin: '0 0 5px 0px',
    position: 'relative',
    textAlign: 'left',
  },
  totalAmountSubContainer: {
    textAlign: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '10px',
    position: 'relative',
    flex: '1',
    alignItems: 'center',
    justifyContent: 'center',
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      width: '1px',
      backgroundColor: '#ddd',
    },
  },
  totalAmountContainer: {
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    padding: '10px',
    borderBottom: '1px solid #ddd',
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalHeading: {
    fontSize: '14px',
    fontWeight: 450,
  },
  totalValue: {
    fontSize: '14px',
    fontWeight: 450,
    color: COLORS.DARK_PINK,
  },
})
